/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create your first sync", description = "Connect a source, build a model, connect a destination, and run your first Hightouch sync end to end.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create your first sync",
        description: "Connect a source, build a model, connect a destination, and run your first Hightouch sync end to end.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Connect a source",
          "depth": 0,
          "id": "connect-a-source"
        }, {
          "value": "Set up a model",
          "depth": 0,
          "id": "set-up-a-model"
        }, {
          "value": "Connect a destination",
          "depth": 0,
          "id": "connect-a-destination"
        }, {
          "value": "Configure the sync",
          "depth": 0,
          "id": "configure-the-sync"
        }, {
          "value": "Run and verify your first sync",
          "depth": 0,
          "id": "run-and-verify-your-first-sync"
        }, {
          "value": "If the result is unexpected",
          "depth": 0,
          "id": "if-the-result-is-unexpected"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      ol: "ol",
      li: "li",
      a: "a",
      h2: "h2",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams setting up their first activation in Hightouch"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "A Hightouch workspace with permission to create sources, models, destinations, and syncs."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.em, {
          children: "A sync sends the rows your model returns to a destination — a tool or app your team already works in."
        }), " This guide takes you from an empty workspace to a running sync in four steps."]
      }), "\n", _jsx(_components.p, {
        children: "A sync depends on three things existing first, so you build them in order:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#connect-a-source",
            children: _jsx(_components.strong, {
              children: "Connect a source"
            })
          }), " — where your data lives."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-model",
            children: _jsx(_components.strong, {
              children: "Set up a model"
            })
          }), " — the query that selects the rows to send."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#connect-a-destination",
            children: _jsx(_components.strong, {
              children: "Connect a destination"
            })
          }), " — where the data lands."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#configure-the-sync",
            children: _jsx(_components.strong, {
              children: "Configure the sync"
            })
          }), " — how and when the rows are sent."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you already have a source, model, or destination, skip ahead to ", _jsx(_components.a, {
          href: "#configure-the-sync",
          children: "Configure the sync"
        }), ". New to Hightouch? Review the ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "core concepts"
        }), " first."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-a-source",
        children: "Connect a source"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/getting-started/concepts#sources",
          children: "source"
        }), " is where your organization's data lives, such as a data warehouse or database."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/sources?from=docs",
            children: [_jsx(_components.strong, {
              children: "Sources"
            }), " overview"]
          }), " and click ", _jsx(_components.strong, {
            children: "Add source"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the source you want to add."
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To test things out without a production source, use the B2B SaaS or B2C\neCommerce ", _jsx(_components.a, {
            href: "/sources/sample-data",
            children: _jsx(_components.strong, {
              children: "Sample datasets"
            })
          }), ". They're at the end of\nthe source list and need no credentials."]
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Enter the source's credentials. These differ by source. See the ", _jsx(_components.a, {
            href: "/sources/overview",
            children: "source documentation"
          }), " for details."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), ". Hightouch tests the connection before it saves. If the test fails, re-enter your credentials."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give the source a ", _jsx(_components.strong, {
            children: "Source name"
          }), ". Include the environment and data it holds, especially if you'll connect more than one instance of the same source, such as a development and a production version."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The source now appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview"]
        }), ", ready to build models against."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-a-model",
        children: "Set up a model"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " defines which rows to pull from your source."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/models?from=docs",
            children: [_jsx(_components.strong, {
              children: "Models"
            }), " overview"]
          }), " and click ", _jsx(_components.strong, {
            children: "Add model"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the source you connected."
        }), "\n", _jsxs(_components.li, {
          children: ["Choose a ", _jsx(_components.a, {
            href: "/models/creating-models",
            children: "modeling method"
          }), ": SQL editor, table selector, dbt model, or Looker Look. The ", _jsx(_components.a, {
            href: "/sources/sample-data",
            children: "Sample datasets"
          }), " come with pre-populated SQL."]
        }), "\n", _jsxs(_components.li, {
          children: ["Preview the query, then click ", _jsx(_components.strong, {
            children: "Continue"
          }), ". The preview shows the first 100 rows so you can confirm the model returns the records you intend to activate before you go further."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give the model a ", _jsx(_components.strong, {
            children: "Model name"
          }), " that describes its contents and purpose."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.strong, {
            children: "Primary key"
          }), " — a column whose values are unique and stable for every row. Change data capture uses the primary key to track which rows were added, changed, or removed between runs, so a stable key is what lets syncs send diffs instead of everything. See ", _jsx(_components.a, {
            href: "/syncs/cdc",
            children: "change data capture"
          }), " for why this matters."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The model now appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/models?from=docs",
          children: [_jsx(_components.strong, {
            children: "Models"
          }), " overview"]
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-a-destination",
        children: "Connect a destination"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/getting-started/concepts#destinations",
          children: "destination"
        }), " is the tool or app you want to receive your data. Connect it before you configure the sync."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/destinations?from=docs",
            children: [_jsx(_components.strong, {
              children: "Destinations"
            }), " overview"]
          }), " and click ", _jsx(_components.strong, {
            children: "Add destination"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the destination you want to add and click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To test things out without a production destination, use ", _jsx(_components.a, {
            href: "/destinations/google-sheets",
            children: "Google\nSheets"
          }), ". It's a ", _jsx(_components.a, {
            href: "/pricing/ss-pricing",
            children: "lite\ndestination"
          }), " and quick to set up."]
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Authorize the connection or enter the destination's credentials. These differ by destination. See the ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination documentation"
          }), " for details."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), ". Hightouch tests the connection before it saves. If the test fails, re-enter your credentials."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give the destination a ", _jsx(_components.strong, {
            children: "Destination name"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The destination now appears on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview"]
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "configure-the-sync",
        children: "Configure the sync"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.a, {
          href: "/getting-started/concepts#syncs",
          children: "sync"
        }), " declares how your model's rows appear in the destination. With a source, model, and destination in place, create it in the sync wizard."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: [_jsx(_components.strong, {
              children: "Syncs"
            }), " overview"]
          }), " and click ", _jsx(_components.strong, {
            children: "Add sync"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the model to pull data from."
        }), "\n", _jsx(_components.li, {
          children: "Select the destination to send data to."
        }), "\n", _jsx(_components.li, {
          children: "Configure how the sync writes to the destination. The options here are destination-specific, but most syncs involve three decisions:"
        }), "\n"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Decision"
            }), _jsx(_components.th, {
              children: "What you choose"
            }), _jsx(_components.th, {
              children: "Guidance"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sync type and mode"
            }), _jsx(_components.td, {
              children: "The object to write to and whether to insert, update, or upsert records."
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/types-and-modes",
                children: "Choose a sync type and mode"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Record matching"
            }), _jsx(_components.td, {
              children: "The destination field and model column used to find the right record."
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/record-matching",
                children: "Match records to destination records"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Field mapping"
            }), _jsx(_components.td, {
              children: "Which model columns map to which destination fields."
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/mapping-data",
                children: "Map model columns to destination fields"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name the sync and set its schedule. A schedule is required — even \"manual only\" is a choice. See ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui",
            children: "schedule a sync"
          }), " for the available schedule types."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You land on the sync's overview page, where you can run it, edit its ", _jsx(_components.strong, {
          children: "Configuration"
        }), " and ", _jsx(_components.strong, {
          children: "Schedule"
        }), ", set up ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerts"
        }), ", and open the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "debugger"
        }), " to inspect past runs."]
      }), "\n", _jsx(_components.h2, {
        id: "run-and-verify-your-first-sync",
        children: "Run and verify your first sync"
      }), "\n", _jsxs(_components.p, {
        children: ["The sync doesn't run on its own until its schedule fires. To run it now, click ", _jsx(_components.strong, {
          children: "Run"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When the run finishes, confirm it did what you expected:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Row count"
          }), " — the number of rows the sync attempted matches the row count from your model preview."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Operation type"
          }), " — the run added, changed, or removed the rows you expected. The first run of an upsert sync usually adds every row."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Rejected rows"
          }), " — the run rejected zero rows. A run can succeed at the top level while individual rows fail, so check this even on a successful run."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Destination record"
          }), " — open one record in the destination and confirm the mapped fields arrived with the right values."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "if-the-result-is-unexpected",
        children: "If the result is unexpected"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Rejected rows or a failed run"
          }), " — open the run and use the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "debugger"
          }), " to inspect the request and response for a specific row."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "A known error message"
          }), " — look it up in the ", _jsx(_components.a, {
            href: "/developer-tools/error-codes",
            children: "sync error codes"
          }), " reference."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Wrong records updated"
          }), " — recheck your ", _jsx(_components.a, {
            href: "/syncs/record-matching",
            children: "record matching"
          }), " column. A non-unique matching field can update the wrong destination record."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Wrong or missing field values"
          }), " — recheck your ", _jsx(_components.a, {
            href: "/syncs/mapping-data",
            children: "field mapping"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/schedule-sync-ui",
            children: "Schedule the sync"
          }), " to run on the cadence your workflow needs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/alerting",
            children: "Configure alerts"
          }), " so you're notified when a run fails."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/cdc",
            children: "Understand change data capture"
          }), " to see how Hightouch decides what to send on each run."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
